import * as React from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Spinner,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { compact, xor } from 'lodash'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import ScrollableList from 'modules/core/components/ScrollableList'
import SimpleUserSelector from 'modules/core/components/SimpleUserSelector'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import employees from 'modules/employees/store/collections/employees'
import { events } from 'modules/reports/lib/events'
import { ReportShares } from 'modules/reports/store/collections/reports_shares'
import type Report from 'modules/reports/store/models/Report'
import {
  ReportShareVisibilities,
  type ReportShareVisibility,
} from 'modules/reports/store/models/ReportShare'

import AddPeopleCollapsableCardTrigger from './AddPeopleCollapsableCardTrigger'
import Owner from './Owner'
import Sharee from './Sharee'
import { ReportShareInfo, useReportSharesEditor } from './useReportSharesEditor'

interface Props {
  report: Report
  onClose: () => void
}

function ShareReportModal({ report, onClose }: Props): JSX.Element {
  const { loading: loadingShares } = useAsyncData(async () => {
    const fetchReportShares = async () => {
      const reportShares = await ReportShares.fetchForReport(report.id)
      editor.reset(reportShares)
    }

    return Promise.all([fetchReportShares(), users.fetch()])
  }, [report.id])

  const editor = useReportSharesEditor(report, users.current.id)
  const [selectedUserIds, setSelectedUserIds] = React.useState<number[]>([])
  const owner = getOwner(report)
  const [calloutVisible, setCalloutVisible] = React.useState(true)
  const [addPeopleOpened, setAddPeopleOpened] = React.useState(false)
  const toggleAddPeopleOwned = () => {
    track(events.clickReportShareAddNewPeople)
    setAddPeopleOpened(!addPeopleOpened)
  }
  const [savedShare, toggleSavedShare] = useToggle(false)
  const shouldDisplayContent = !loadingShares && !savedShare

  const handleSelect = (id: number | null | undefined) => {
    const selection = id ? xor(selectedUserIds, [id]) : []
    setSelectedUserIds(selection)
  }

  const handleMultiSelect = (ids: number[]) => {
    setSelectedUserIds(ids)
  }

  const handleAddUsers = () => {
    track(events.ReportShareEmployeesSelected)
    const shares = selectedUserIds.map((id) => ({
      granteeId: id,
      visibility: ReportShareVisibilities.Viewer,
    }))

    editor.addShares(shares)

    setAddPeopleOpened(false)
    setSelectedUserIds([])
  }

  const handleCancel = () => onClose()

  const handleSave = async () => {
    track(events.submitReportShare)
    await editor.saveChanges()
    toggleSavedShare(true)()
    onClose()
  }

  const addableUsers = resolveAddableUsers(
    users.active(employees),
    report.authorId,
    editor.currentShares
  )

  const content = () => {
    return (
      <>
        <Box gap="s24">
          {calloutVisible && (
            <Callout
              description={i18n.t('custom_reports.share.modal.callout.description')}
              hideIcon
              type="info"
              onRemove={() => {
                setCalloutVisible(false)
              }}
            />
          )}
          <Box flexGrow width="full">
            <CollapsableCard
              open={addPeopleOpened}
              Card={
                <AddPeopleCollapsableCardTrigger onClick={toggleAddPeopleOwned} icon="add">
                  {i18n.t('custom_reports.share.modal.add_people.button')}
                </AddPeopleCollapsableCardTrigger>
              }
              Content={
                <Box gap="s24">
                  <SimpleUserSelector
                    loadingOptions={false}
                    users={addableUsers}
                    onSelect={handleSelect}
                    onSelectMultiple={handleMultiSelect}
                    selectedIds={selectedUserIds}
                  />
                  <Button onClick={handleAddUsers} disabled={selectedUserIds.length < 1}>
                    {selectedUserIds.length
                      ? i18n.tp('custom_reports.share.modal.add_people.popover_button.selection', {
                          count: selectedUserIds.length,
                        })
                      : i18n.t('custom_reports.share.modal.add_people.popover_button.no_selection')}
                  </Button>
                </Box>
              }
            />
          </Box>
          <Box gap="s16">
            <Text.Normal size="300" weight="semibold" color="grey1000">
              {i18n.t('custom_reports.share.modal.list_title')} ({editor.currentShares.length + 1})
            </Text.Normal>

            <ScrollableList size="medium">
              <Box paddingRight="s8" gap="s16">
                {owner && <Owner user={owner} />}
                <Sharees
                  shares={editor.currentShares}
                  onVisibilityChange={editor.changeVisibility}
                  onDelete={editor.removeShare}
                />
              </Box>
            </ScrollableList>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <Modal
      title={renderModalTitle(report.title)}
      description={renderModalDescription()}
      illustration="document-edit"
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <Box flexDirection="column" hideScrollBar={false}>
        <Box minHeight="s80">
          {loadingShares && (
            <Box alignItems="center" paddingY="s16">
              <Spinner />
            </Box>
          )}
          {shouldDisplayContent && content()}
        </Box>
      </Box>
    </Modal>
  )
}

function resolveAddableUsers(
  all_users: User[],
  authorId: number,
  existingShares: ReportShareInfo[]
): User[] {
  const shareesIds = existingShares.map((share) => share.granteeId)
  const hiddenUserIds = new Set([authorId, ...shareesIds])

  return all_users.filter((user) => !hiddenUserIds.has(user.id))
}

function renderModalTitle(reportTitle: string) {
  return i18n.t('custom_reports.share.modal.title', { reportTitle: reportTitle })
}

function renderModalDescription() {
  return i18n.t('custom_reports.share.modal.description_simplified')
}

function getOwner(report: Report): User | null {
  const owner = users.get(report.authorId)

  return owner ?? null
}

interface ShareesProps {
  shares: ReportShareInfo[]
  onVisibilityChange: (shareeId: number, visibility: ReportShareVisibility) => void
  onDelete: (shareeId: number) => void
}

function Sharees({ shares, onVisibilityChange, onDelete }: ShareesProps): JSX.Element {
  const sharees = compact(
    shares.map((share) => {
      const user = users.get(share.granteeId)

      if (!user) {
        return
      }

      return {
        share: share,
        user: user,
      }
    })
  )

  return (
    <Box gap="s16">
      {sharees.map((s) => (
        <Sharee
          key={s.share.granteeId}
          user={s.user}
          mode={s.share.mode}
          visibility={s.share.visibility}
          onVisibilityChange={(visibility) => onVisibilityChange(s.share.granteeId, visibility)}
          onDelete={() => onDelete(s.share.granteeId)}
        />
      ))}
    </Box>
  )
}

export default observer(ShareReportModal)
